import { useMutation } from "@apollo/client";
import { gql } from "@/gql";
import type { AppointmentDetailFragment } from "@/gql/graphql";
import { APPOINTMENT_QUERY } from "@/graphql/appointments";
import { handleGraphQLErrors } from "@/lib/error/apollo-utils";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";

const SET_APPOINTMENT_SERVICE_PRICE_MUTATION = gql(`
  mutation SetAppointmentServicePrice($salonId: ID!, $appointmentId: ID!, $input: SetAppointmentServicePriceInput!) {
    setAppointmentServicePrice(salonId: $salonId, appointmentId: $appointmentId, input: $input) {
      ...AppointmentDetail @unmask
    }
  }
`);

interface SetServicePriceInput {
  services: Array<{
    appointmentServiceId: string;
    priceInCents: number;
  }>;
  notifyCustomer?: boolean;
}

export const useSetAppointmentServicePrice = () => {
  const currentSalon = useSalonStore((state) => state.currentSalon);

  const [setAppointmentServicePriceMutation] = useMutation(SET_APPOINTMENT_SERVICE_PRICE_MUTATION, {
    errorPolicy: "all",
  });

  const { execute, isLoading } = useAsyncAction(
    async (
      appointmentId: string,
      input: SetServicePriceInput
    ): Promise<AppointmentDetailFragment | null> => {
      if (!currentSalon?.id) {
        throw new Error("No salon selected");
      }

      const result = await setAppointmentServicePriceMutation({
        variables: {
          salonId: currentSalon.id,
          appointmentId,
          input,
        },
        refetchQueries: [
          {
            query: APPOINTMENT_QUERY,
            variables: {
              salonId: currentSalon.id,
              id: appointmentId,
            },
          },
        ],
        awaitRefetchQueries: true,
      });

      if (result.errors) {
        handleGraphQLErrors(result.errors);
      }

      return result.data?.setAppointmentServicePrice ?? null;
    },
    {
      loadingMessage: "Updating service prices...",
      successMessage: "Service prices updated successfully",
      errorMessage: "Failed to update service prices",
    }
  );

  return {
    setServicePrice: execute,
    isUpdating: isLoading,
  };
};
